import { Messages } from "@/components/ui";
import { useDebounce } from "@/hooks/useDebounce";
import { CASE_MEMBER_TYPE, QUERY_KEYS } from "@/libraries/constant";
import { isAxiosError } from "@/libraries/helper";
import {
	getCaseQueryOptions,
	getMessagesQueryOptions,
	markReadAllMessageQueryOptions,
} from "@/queries";
import {
	CASE_TABS_VALUE,
	CaseChatSearchSchema,
	PostCreateMessageBodySchema,
} from "@/schemas";
import type { GetCaseResponse, GroupMember } from "@/types";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	type QueryClient,
	onlineManager,
	useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { zodSearchValidator } from "@tanstack/router-zod-adapter";
import { useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import AppBarCaseChat from "../../-components/AppBarChat";
import EmptyCase from "../../-components/Empty";
import PendingComponent from "./-components/PendingComponent";

export const CASE_CHAT_TABS = [
	{
		label: "All",
		value: CASE_TABS_VALUE.ALL,
	},
	{
		label: "HEMI AI",
		value: CASE_TABS_VALUE.AI,
	},
	{
		label: "Diagnosis",
		value: CASE_TABS_VALUE.DIAGNOSIS,
	},
	{
		label: "Plan",
		value: CASE_TABS_VALUE.PLAN,
	},
	{
		label: "Files",
		value: CASE_TABS_VALUE.FILES,
	},
	{
		label: "External",
		value: CASE_TABS_VALUE.EXTERNAL,
	},
];

export const Route = createFileRoute("/dashboard/case/view/$id/")({
	validateSearch: zodSearchValidator(CaseChatSearchSchema),
	loaderDeps: ({ search }) => ({ tags: search.tab, subTags: search.subtab }),
	beforeLoad: async ({ context, params, cause }) => {
		const queryClient: QueryClient = context.queryClient;
		if (cause === "enter") {
			const caseItem = await queryClient.fetchQuery(
				getCaseQueryOptions(params.id),
			);
			if (caseItem.unread_main_chats && caseItem.channel_name)
				await queryClient.prefetchQuery(
					markReadAllMessageQueryOptions({
						channel_name: caseItem.channel_name,
					}),
				);
		}
	},
	loader: async ({ context, params, deps }) => {
		try {
			if (onlineManager.isOnline() && !context.isRestoring) {
				const caseItem: GetCaseResponse =
					await context.queryClient.ensureQueryData(
						getCaseQueryOptions(params.id),
					);
				const messages = await context.queryClient.ensureInfiniteQueryData(
					getMessagesQueryOptions({
						channel_name: String(caseItem.channel_name),
						tags: deps.tags,
					}),
				);
				return [caseItem, messages];
			}
			const caseItem = context.queryClient.getQueryData([
				QUERY_KEYS.GET_CASE,
				params.id,
			]) as GetCaseResponse;
			const messages = context.queryClient.getQueryData([
				QUERY_KEYS.GET_MESSAGES,
				{
					channel_name: String(caseItem.channel_name),
					tags: deps.tags === CASE_TABS_VALUE.ALL ? undefined : deps.tags,
				},
			]);
			return [caseItem, messages];
		} catch (error) {
			if (isAxiosError(error, 404)) {
				throw notFound();
			}
		}
	},
	component: RouteComponent,
	pendingComponent: PendingComponent,
});

function RouteComponent() {
	const navigate = Route.useNavigate();
	const caseId = Route.useParams({
		select: (state) => state.id,
	});
	const search = Route.useSearch({
		select: (search) => search.q || "",
	});
	const tab = Route.useSearch({
		select: (search) => search.tab,
	});
	const subTab = Route.useSearch({
		select: (search) => search.subtab,
	});
	const searcParamsTags = Route.useSearch({
		select: (search) => search.tags,
	});
	const searcParamsFocusMessageId = Route.useSearch({
		select: (search) => search.mid,
	});
	const debounceQ = useDebounce(search, 500);
	const form = useForm<PostCreateMessageBodySchema>({
		mode: "all",
		resolver: zodResolver(PostCreateMessageBodySchema),
		defaultValues: async () => ({
			channel_name: caseItem?.channel_name,
		}),
	});

	const { data: caseItem } = useSuspenseQuery(getCaseQueryOptions(caseId));
	const caseMembers = useMemo<GroupMember[]>(() => {
		return [...caseItem.members, ...caseItem.pending_members];
	}, [caseItem]);

	const onChangeTab = (val: string) => {
		return navigate({
			search: {
				tab: val,
			},
			replace: true,
		});
	};

	const onClickSearch = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		return navigate({
			to: "/dashboard/case/view/$id/search",
			params: {
				id: caseId,
			},
		});
	};

	const onChangeSubTab = (
		e: React.MouseEvent<HTMLElement>,
		value: string | null,
	) => {
		e.preventDefault();
		return navigate({
			replace: true,
			search: (oldSearch: CaseChatSearchSchema) => ({
				...oldSearch,
				subtab: value === "all" ? undefined : (value ?? undefined),
			}),
		});
	};

	useEffect(() => {
		form.setValue(
			"tags",
			searcParamsTags ?? (tab && tab !== "all" ? tab : null),
			{
				shouldDirty: true,
				shouldTouch: true,
			},
		);
	}, [searcParamsTags, tab, form.setValue]);

	return (
		<>
			<AppBarCaseChat
				caseItem={caseItem}
				tabs={CASE_CHAT_TABS}
				activeTab={tab}
				activeSubTab={subTab}
				onChangeTab={onChangeTab}
				onClickSearch={onClickSearch}
				onChangeSubTab={onChangeSubTab}
			/>
			<FormProvider {...form}>
				<Messages
					module="cases"
					isReadonly={caseItem.status === "closed"}
					channelType={null}
					channelName={caseItem?.channel_name}
					search={{
						search: debounceQ,
						tab,
						subTab,
					}}
					members={caseMembers}
					focusId={searcParamsFocusMessageId}
					isViewOnly={
						caseItem.member_type === CASE_MEMBER_TYPE.EXTERNAL_REQUEST_MEMBER
					}
					renderEmpty={<EmptyCase type="chat" title="No discussion yet" />}
				/>
			</FormProvider>
		</>
	);
}
